import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import PasswordInput from 'components/password-input-view';
import CloseButton from 'components/close-button-view';
import { AnchorIntl } from 'components/intl';
import {
  FormattedMessage,
  FormattedHTMLMessage,
  defineMessages,
  useIntl,
} from 'react-intl';
import st from 'core/shared-translations';
import classes from './signin-security.scss';
import GoogleLogo from 'static/images/svg/google_logo.svg';
import FacebookLogo from 'static/images/svg/facebook_logo.svg';
import LinkedInLogo from 'static/images/svg/linkedin_logo.svg';
import MicrosoftLogo from 'static/images/svg/microsoft_logo.svg';
import LastPassLogo from 'static/images/svg/lastpass_logo.svg';
import AppleLogo from 'static/images/svg/apple_logo.svg';
import appconfig from 'config/appconfig';
import { getModalParent } from 'lib/utility-functions';
import AllSetCheckImage from 'static/images/green_checkmark.png';
import StaticLink from 'components/static-link-view';

const t = defineMessages({
  'password-editor.password': {
    id: 'password-editor.password',
    defaultMessage: 'Password',
    description: 'Title string for the password editor section',
  },
  'password-editor.current-password': {
    id: 'password-editor.current-password',
    defaultMessage: 'Current password',
    description: 'Field label for the current password field',
  },
  'password-editor.edit-password': {
    id: 'password-editor.edit-password',
    defaultMessage: 'Edit Password',
    description: 'Title string for the password editor modal dialog',
  },
  'password-editor.new-password': {
    id: 'password-editor.new-password',
    defaultMessage: 'New password',
    description: 'Field label for the new password field',
  },
  'password-editor.forgot-password': {
    id: 'password-editor.forgot-password',
    defaultMessage: 'Forgot your password?',
    description: 'Link text for triggering the recover password process',
  },
  'password-editor.confirm-password': {
    id: 'password-editor.confirm-password',
    defaultMessage: 'Confirm new password',
    description: 'Field label for the confirm new password field',
  },
  'password-editor.hide-password': {
    id: 'password-editor.hide-password',
    defaultMessage: 'Hide password',
    description: 'Toggle aria-label for when the password is displayed as text',
  },
  'password-editor.show-password': {
    id: 'password-editor.show-password',
    defaultMessage: 'Show password',
    description: 'Toggle aria-label for when the password is displayed as hidden characters',
  },
  'password-editor.letters-numbers': {
    id: 'password-editor.letters-numbers',
    defaultMessage: 'Letters & numbers',
    description: 'Letters and numbers match validation indicator line 1 for new password entry',
  },
  'password-editor.minimum-characters': {
    id: 'password-editor.minimum-characters',
    defaultMessage: '8 characters minimum',
    description: 'Minimum characters match validation indicator line 2 for new password entry',
  },
  'password-editor.validation-error-password': {
    id: 'password-editor.validation-error-password',
    defaultMessage: 'Enter a password with 8 to 32 characters.',
    description: 'Content of the error message when the password field is left blank or contains invalid characters',
  },
  'password-editor.mismatched-passwords': {
    id: 'password-editor.mismatched-passwords',
    defaultMessage: 'Enter a matching password.',
    description: 'Content of the error message when the New and Confirm password fields value does not match',
  },
  'password-editor.unlink-last-social-message': {
    id: 'password-editor.unlink-last-social-message',
    defaultMessage: 'To stay signed in, you can connect through a different social sign-in, or create a new password to sign in directly.',
    description: 'Instructions displayed when the last social account is being unlinked',
  },
  'password-editor.all-set': {
    id: 'password-editor.all-set',
    defaultMessage: 'All Set!',
    description: 'Title string for the All Set editor section',
  },
  'password-editor.account-reset': {
    id: 'password-editor.account-reset',
    defaultMessage: 'You\'ve disconnected your {socialAccount} account.',
    description: 'Title string for the All Set editor section',
  },
  'password-editor.forward-login-instructions': {
    id: 'password-editor.forward-login-instructions',
    defaultMessage: 'You can now sign in using your new password and email address <b>{userName}</b>.',
    description: 'Title string for the All Set editor section',
  },
  'password-editor.save-password-failed': {
    id: 'password-editor.save-password-failed',
    defaultMessage: 'Enter your current password correctly.',
    description: 'Content of the error message when the password save has failed.',
  },
  'password-editor.save-password-failed-reused-password': {
    id: 'password-editor.save-password-failed-reused-password',
    defaultMessage: 'Enter a password that is not the same as your last 5 passwords to meet the security requirements of your account.',
    description: 'Content of the error message when the password save has failed due to a reused password under password rotation policy',
  },
  'password-editor.validation-missing-current-password': {
    id: 'password-editor.validation-missing-current-password',
    defaultMessage: 'Password is a required field',
    description: 'Content of the error message when the current password field is left empty',
  },
});

const maxLength = '32';

const PasswordEditor = ({
  personData,
  onEditLinkClick,
  modalIsOpen,
  closeModal,
  currentPassword,
  newPassword,
  confirmPassword,
  forgotPasswordOnClick,
  lettersNumbersReqMet,
  minCharacterCountReqMet,
  onViewAsTextClick,
  onViewAsTextByKeyboard,
  viewPasswordAsText,
  onChange,
  onBlur,
  onSave,
  onFocus,
  enableSubmit,
  passwordSet,
  passwordLoginEnabled,
  isLoading,
  errors,
  disconnectLastSocialAccount,
  socialLinksToDisplay,
  closeAllSetModal,
  disconnectedSocialAccountName,
  showAllSetTitle,
  passwordEditorDisplayClass,
  allSetClass,
  storeDisconnectedSocialConnection,
  isViewOnly = false,
}) => {
  const intl = useIntl();
  return (
    <div>
      <div className="row">
        <div id="password-section-title" className="col-xs-9 sectionTitle">
          <FormattedMessage {...t['password-editor.password']} />
        </div>
        {passwordLoginEnabled &&
          <div className={`col-xs-3 no-padding ${classes[isViewOnly ? 'editLinkViewOnly': 'editLink']}`}>
            {isViewOnly &&
              <StaticLink
                value={intl.formatMessage(st['shared.edit'])}
                showEditIcon
              />}
            {!isViewOnly &&
              <a
                id="password-edit-link"
                href="#" onClick={onEditLinkClick}
                aria-label={intl.formatMessage(t['password-editor.edit-password'])}
                title={intl.formatMessage(t['password-editor.edit-password'])}
              >
                <i className="togo-icon togo-icon-edit" />
                &nbsp;
                <FormattedMessage {...st['shared.edit']} />
              </a>}
          </div>}
      </div>
      <div id="password-set" className={classes['staticFieldValue']} style={{paddingTop: '8px'}}>
        {passwordSet && passwordLoginEnabled ?
          '**********' :
          <FormattedMessage {...st['shared.none-set']} />}
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="EditPasswordModal"
        shouldCloseOnOverlayClick={false}
        className={classes['modal']}
        overlayClassName={classes['overlay']}
        parentSelector={getModalParent}
      >
        <CloseButton
          ariaLabel={intl.formatMessage(st['shared.close'])}
          onClick={closeModal}
        />
        <div id="password-modal-title" className="modalTitle">
          {(!disconnectLastSocialAccount && !showAllSetTitle) &&
            <FormattedMessage {...t['password-editor.edit-password']} />}
          {(disconnectLastSocialAccount && !showAllSetTitle) &&
            <FormattedMessage {...st['shared.almost-there']} />}
          {showAllSetTitle &&
            <FormattedMessage {...t['password-editor.all-set']} />}
        </div>
        <div className={`modalContent ${passwordEditorDisplayClass}`}>
          <div className="contentDivPadding">
            {!disconnectLastSocialAccount && passwordSet &&
              <PasswordInput
                id="currentPassword"
                name="currentPassword"
                label={intl.formatMessage(t['password-editor.current-password'])}
                helpText=""
                value={currentPassword}
                maxLength="255"
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                inputType={viewPasswordAsText.currentPassword ? 'text' : 'password'}
                onViewAsTextClick={onViewAsTextClick}
                onViewAsTextByKeyboard={onViewAsTextByKeyboard}
                toggleAriaLabel={intl.formatMessage(t[viewPasswordAsText.currentPassword ? 'password-editor.hide-password' : 'password-editor.show-password'])}
                showForgotPassword={{
                  label: intl.formatMessage(t['password-editor.forgot-password']),
                  href: forgotPasswordOnClick,
                }}
                error={
                  errors.currentPassword &&
                  intl.formatMessage(st[errors.currentPassword] || t[errors.currentPassword])
                }
              />}
            {disconnectLastSocialAccount &&
              <div id="unlinkLastSocialAccountMessage" className={classes['passwordEditUserInstruction']}>
                <FormattedMessage {...t['password-editor.unlink-last-social-message']} />
              </div>}
            {disconnectLastSocialAccount &&
              <div className="row">
                {typeof socialLinksToDisplay[appconfig.socialAccounts.LASTPASS] !== 'undefined' &&
                  <div id="connectWithLastPass" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.LASTPASS]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={LastPassLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.LASTPASS],
                              message: st['shared.connect-to-lastpass'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
                {typeof socialLinksToDisplay[appconfig.socialAccounts.GOOGLE] !== 'undefined' &&
                  <div id="connectWithGoogle" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.GOOGLE]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={GoogleLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.GOOGLE],
                              message: st['shared.connect-to-google'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
                {typeof socialLinksToDisplay[appconfig.socialAccounts.FACEBOOK] !== 'undefined' &&
                  <div id="connectWithFaceBook" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.FACEBOOK]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={FacebookLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.FACEBOOK],
                              message: st['shared.connect-to-facebook'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
                {typeof socialLinksToDisplay[appconfig.socialAccounts.LINKEDIN] !== 'undefined' &&
                  <div id="connectWithLinkedIn" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.LINKEDIN]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={LinkedInLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.LINKEDIN],
                              message: st['shared.connect-to-linkedin'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
                {typeof socialLinksToDisplay[appconfig.socialAccounts.MICROSOFT] !== 'undefined' &&
                  <div id="connectWithMicrosoft" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.MICROSOFT]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={MicrosoftLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.MICROSOFT],
                              message: st['shared.connect-to-microsoft'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
                {typeof socialLinksToDisplay[appconfig.socialAccounts.APPLE] !== 'undefined' &&
                  <div id="connectWithApple" className={`col-xs-12 col-sm-6 ${classes['siColumn']}`}>
                    <div className="row">
                      <div className="col-xs-2">
                        <a
                          href={socialLinksToDisplay[appconfig.socialAccounts.APPLE]}
                          target="_self"
                          onClick={storeDisconnectedSocialConnection()}
                        >
                          <img className={classes['socialLogo']} src={AppleLogo} alt="" />
                        </a>
                      </div>
                      <div className="col-xs-10">
                        <div className={classes['socialLabel']}>
                          <AnchorIntl
                            a={{
                              href: socialLinksToDisplay[appconfig.socialAccounts.APPLE],
                              message: st['shared.connect-to-apple'],
                              target: '_self',
                              onClick: storeDisconnectedSocialConnection()}}
                          />
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>}
            {disconnectLastSocialAccount &&
              <div id="orSeparator" className={classes['passwordEditOrSeparator']}>
                <div className={classes['orDash']} />
                <FormattedMessage {...st['shared.or']} />
                <div className={classes['orDash']} />
              </div>}
            <PasswordInput
              id="newPassword"
              name="newPassword"
              label={intl.formatMessage(t['password-editor.new-password'])}
              helpText=""
              value={newPassword}
              maxLength={maxLength}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              inputType={viewPasswordAsText.newPassword ? 'text' : 'password'}
              lettersNumbersRequirement={{
                label: intl.formatMessage(t['password-editor.letters-numbers']),
                isMet: lettersNumbersReqMet,
              }}
              minCharacterCountRequirement={{
                label: intl.formatMessage(t['password-editor.minimum-characters']),
                isMet: minCharacterCountReqMet,
              }}
              onViewAsTextClick={onViewAsTextClick}
              onViewAsTextByKeyboard={onViewAsTextByKeyboard}
              toggleAriaLabel={intl.formatMessage(t[viewPasswordAsText.newPassword ? 'password-editor.hide-password' : 'password-editor.show-password'])}
              error={
                errors.newPassword &&
                intl.formatMessage(st[errors.newPassword] || t[errors.newPassword])
              }
            />
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label={intl.formatMessage(t['password-editor.confirm-password'])}
              value={confirmPassword}
              maxLength={maxLength}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              inputType={viewPasswordAsText.confirmPassword ? 'text' : 'password'}
              toggleAriaLabel={intl.formatMessage(t[viewPasswordAsText.confirmPassword ? 'password-editor.hide-password' : 'password-editor.show-password'])}
              onViewAsTextClick={onViewAsTextClick}
              onViewAsTextByKeyboard={onViewAsTextByKeyboard}
              error={
                errors.confirmPassword &&
                intl.formatMessage(st[errors.confirmPassword] || t[errors.confirmPassword])
              }
            />
          </div>
          <div className="row buttonRow">
            <div className="col-xs-12 col-sm-6 no-gutter">
              <button
                id="password-modal-save-button"
                type="button"
                disabled={!enableSubmit}
                className="btn btn-default ninety-button marginl-5"
                onClick={onSave}>
                {isLoading ?
                  intl.formatMessage(st['shared.saving']) :
                  intl.formatMessage(st['shared.save'])}
              </button>
            </div>
            <div className="col-xs-12 col-sm-6 no-gutter">
              <button
                id="password-modal-cancel-button"
                type="button"
                className="btn btn-tertiary ninety-button"
                onClick={closeModal}>
                {intl.formatMessage(st['shared.cancel'])}
              </button>
            </div>
          </div>
        </div>
        <div id="allSet" className={`modalContent ${allSetClass}`}>
          <div className="contentDivPadding">
            <div className="text-center">
              <img className={classes['allSetImage']} src={AllSetCheckImage} alt="" />
            </div>
            <div className={classes['userInstruction']}>
              <FormattedMessage {...t['password-editor.account-reset']} values={{socialAccount: disconnectedSocialAccountName}} />
            </div>
            <div className={classes['userInstruction']}>
              <FormattedHTMLMessage {...t['password-editor.forward-login-instructions']} values={{userName: personData.userName}} />
            </div>
          </div>
          <div className="row buttonRow" style={{marginTop: '6px'}}>
            <div className="col-xs-12">
              <button
                id="password-modal-closeAllSet-button"
                type="button"
                className="btn btn-default ninety-button"
                style={{maxWidth: '140px'}}
                onClick={closeAllSetModal}>
                {intl.formatMessage(st['shared.close'])}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

PasswordEditor.propTypes = {
  personData: PropTypes.object.isRequired,
  onEditLinkClick: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  passwordSet: PropTypes.bool.isRequired,
  passwordLoginEnabled: PropTypes.bool.isRequired,
  currentPassword: PropTypes.string,
  newPassword: PropTypes.string,
  lettersNumbersReqMet: PropTypes.bool.isRequired,
  minCharacterCountReqMet: PropTypes.bool.isRequired,
  confirmPassword: PropTypes.string,
  forgotPasswordOnClick: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  enableSubmit: PropTypes.bool.isRequired,
  onViewAsTextClick: PropTypes.func.isRequired,
  onViewAsTextByKeyboard: PropTypes.func.isRequired,
  viewPasswordAsText: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  disconnectLastSocialAccount: PropTypes.bool.isRequired,
  socialLinksToDisplay: PropTypes.object.isRequired,
  closeAllSetModal: PropTypes.func.isRequired,
  disconnectedSocialAccountName: PropTypes.string.isRequired,
  showAllSetTitle: PropTypes.bool.isRequired,
  passwordEditorDisplayClass: PropTypes.string.isRequired,
  allSetClass: PropTypes.string.isRequired,
  storeDisconnectedSocialConnection: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool,
};

export default PasswordEditor;
